import { useState, useContext, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { AlertContext } from "../../context/AlertContext";
import { domain } from "../../consts";
import StepEventBasics from "./steps/StepEventBasics";
import StepChooseTemplate from "./steps/StepChooseTemplate";
import StepCustomizeDesign from "./steps/StepCustomizeDesign";
import StepEventsTimeline from "./steps/StepEventsTimeline";
import StepFinalSettings from "./steps/StepFinalSettings";
import WizardProgress from "./WizardProgress";
import InvitationPreview from "../InvitationPreview";

const STEPS_CREATE = [
  { id: 1, title: "Osnovne informacije", description: "Ime i datum" },
  { id: 2, title: "Predložak", description: "Odaberi stil" },
  { id: 3, title: "Dizajn", description: "Boje i pozadina" },
  { id: 4, title: "Događaji", description: "Vremenska crta" },
  { id: 5, title: "Objavi", description: "URL i paket" },
];

const STEPS_EDIT = [
  { id: 1, title: "Osnovne informacije", description: "Ime i datum" },
  { id: 2, title: "Dizajn", description: "Boje i pozadina" },
  { id: 3, title: "Događaji", description: "Vremenska crta" },
  { id: 4, title: "Spremi", description: "URL i postavke" },
];

const getDefaultFormData = () => ({
  template: 0,
  selectedTemplateId: null,
  eventName: "",
  coupleName1: "",
  coupleName2: "",
  date: new Date(),
  location: "",
  headerImage: "",
  includeHeaderImage: true,
  texture: "",
  timelineWithQuote: true,
  quote: true,
  quoteText: "Veselimo se vašem dolasku!",
  quoteAuthor: "",
  events: [
    {
      id: 1,
      eventName: "Ceremonija vjenčanja",
      time: "14:00",
      location: "",
      icon: "098",
      coordinates: { lat: 43.5080375, lng: 16.440445057010585 },
    },
    {
      id: 2,
      eventName: "Svečani ručak",
      time: "16:00",
      location: "",
      icon: "124",
      coordinates: { lat: 43.5157232, lng: 16.4254321 },
    },
  ],
  countdown: true,
  countdownImage: "",
  includeCountdownImage: false,
  countdownFont: "display",
  countdownColor: "#FFFFFF",
  countdownFontSize: 24,
  countdownIsBold: false,
  countdownIsItalic: false,
  formAccommodation: false,
  formExpiryDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
  bgColor: "#F5F5F0",
  fgColor: "E8E4DE",
  opacity: "0.5",
  pattern: "wiggle",
  patternScale: "1",
  textColor: "#FFFFFF",
  highlightColor: "#D4AF37",
  textFont: "display",
  highlightFont: "display",
  border: false,
  borderColor: "#D4AF37",
  borderWidth: 1,
  slug: "",
  includeForm: true,
  includeEmail: true,
  includePhone: true,
  tier: 0,
  titleFont: "display",
  titleColor: "#FFFFFF",
  titleFontSize: 36,
  titleIsBold: false,
  titleIsItalic: false,
  subtitleFont: "display",
  subtitleColor: "#000000",
  subtitleFontSize: 20,
  subtitleIsBold: false,
  subtitleIsItalic: false,
  dateFont: "display",
  dateColor: "#FFFFFF",
  dateFontSize: 20,
  dateIsBold: false,
  dateIsItalic: false,
  locationFont: "display",
  locationColor: "#000000",
  locationFontSize: 16,
  locationIsBold: false,
  locationIsItalic: false,
  quoteFont: "display",
  quoteColor: "#000000",
  quoteFontSize: 18,
  quoteIsBold: false,
  quoteIsItalic: true,
  eventTitleFont: "display",
  eventTitleColor: "#000000",
  eventTitleFontSize: 18,
  eventTitleIsBold: false,
  eventTitleIsItalic: false,
  eventTimeFont: "display",
  eventTimeColor: "#000000",
  eventTimeFontSize: 16,
  eventTimeIsBold: false,
  eventTimeIsItalic: false,
  eventLocationFont: "display",
  eventLocationColor: "#000000",
  eventLocationFontSize: 16,
  eventLocationIsBold: false,
  eventLocationIsItalic: false,
  formTitleText: "Potvrdite svoj dolazak!",
  formTitleFont: "display",
  formTitleColor: "#1F2937",
  formTitleFontSize: 36,
  formTitleIsBold: false,
  formTitleIsItalic: false,
  formSubtitleText: "",
  formSubtitleFont: "display",
  formSubtitleColor: "#374151",
  formSubtitleFontSize: 20,
  formSubtitleIsBold: false,
  formSubtitleIsItalic: false,
  formSuccessText: "Hvala na potvrdi!",
  formSuccessFont: "display",
  formSuccessColor: "#D4AF37",
  formSuccessFontSize: 36,
  formSuccessIsBold: false,
  formSuccessIsItalic: false,
  formButtonFont: "display",
  formButtonColor: "#D4AF37",
  formButtonFontSize: 24,
  formAddGuestText: "+ ima nas još",
  formAddGuestFont: "display",
  formAddGuestColor: "#D4AF37",
  formAddGuestFontSize: 18,
  formAddGuestIsBold: false,
  formAddGuestIsItalic: false,
  includeMessageToGuests: false,
  includeMap: false,
  mapStyle: "default",
  messageToGuestsText: "",
  messageToGuestsFont: "display",
  messageToGuestsFontSize: 20,
  messageToGuestsColor: "#000000",
  messageToGuestsIsBold: false,
  messageToGuestsIsItalic: false,
  includeEmblem: true,
  emblemLeftLetter: "",
  emblemRightLetter: "",
  emblemFont: "display",
  emblemFontSize: 48,
  emblemColor: "#000000",
  emblemLineColor: "#D4AF37",
  emblemIsBold: false,
  emblemIsItalic: false,
  shareTitle: "",
  shareDescription: "",
});

const InvitationWizard = ({ initialData = null }) => {
  const { handleAlert } = useContext(AlertContext);
  const navigate = useNavigate();
  const isEditing = Boolean(initialData?.id);
  const STEPS = isEditing ? STEPS_EDIT : STEPS_CREATE;
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [formData, setFormData] = useState(initialData || getDefaultFormData());

  const updateFormData = useCallback((updates) => {
    setFormData((prev) => ({ ...prev, ...updates }));
  }, []);

  const validateStep = (step) => {
    if (isEditing) {
      switch (step) {
        case 1:
          if (!formData.eventName.trim()) {
            handleAlert("Molimo unesite ime događaja", "warning");
            return false;
          }
          return true;
        case 2:
        case 3:
          return true;
        case 4:
          if (!formData.slug.trim()) {
            handleAlert("Molimo unesite URL pozivnice", "warning");
            return false;
          }
          return true;
        default:
          return true;
      }
    } else {
      switch (step) {
        case 1:
          if (!formData.eventName.trim()) {
            handleAlert("Molimo unesite ime događaja", "warning");
            return false;
          }
          return true;
        case 2:
          if (!formData.selectedTemplateId) {
            handleAlert("Molimo odaberite predložak", "warning");
            return false;
          }
          return true;
        case 3:
        case 4:
          return true;
        case 5:
          if (!formData.slug.trim()) {
            handleAlert("Molimo unesite URL pozivnice", "warning");
            return false;
          }
          return true;
        default:
          return true;
      }
    }
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => Math.min(prev + 1, STEPS.length));
    }
  };

  const handleBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const handleStepClick = (stepId) => {
    if (stepId < currentStep) {
      setCurrentStep(stepId);
    } else if (stepId === currentStep + 1 && validateStep(currentStep)) {
      setCurrentStep(stepId);
    }
  };

  const handleSubmit = async () => {
    const finalStep = isEditing ? 4 : 5;
    if (!validateStep(finalStep)) return;

    setLoading(true);
    const url = `${domain}/rsvp${formData.id ? "/" + formData.id : ""}`;

    try {
      const response = await fetch(url, {
        method: formData.id ? "PUT" : "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        handleAlert(
          formData.id ? "Pozivnica uspješno ažurirana!" : "Pozivnica uspješno kreirana!",
          "success"
        );
        setTimeout(() => {
          navigate(formData.id ? "/moje-pozivnice" : "/moje-pozivnice?newInvite");
        }, 500);
      } else {
        const errorData = await response.text();
        handleAlert(errorData || "Došlo je do greške. Molimo pokušajte ponovno.", "error");
      }
    } catch (error) {
      handleAlert("Došlo je do greške. Molimo pokušajte ponovno.", "error");
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    const commonProps = {
      formData,
      updateFormData,
      setFormData,
    };

    if (isEditing) {
      switch (currentStep) {
        case 1:
          return <StepEventBasics {...commonProps} />;
        case 2:
          return <StepCustomizeDesign {...commonProps} />;
        case 3:
          return <StepEventsTimeline {...commonProps} />;
        case 4:
          return <StepFinalSettings {...commonProps} />;
        default:
          return null;
      }
    } else {
      switch (currentStep) {
        case 1:
          return <StepEventBasics {...commonProps} />;
        case 2:
          return <StepChooseTemplate {...commonProps} />;
        case 3:
          return <StepCustomizeDesign {...commonProps} />;
        case 4:
          return <StepEventsTimeline {...commonProps} />;
        case 5:
          return <StepFinalSettings {...commonProps} />;
        default:
          return null;
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-6">
        <WizardProgress
          steps={STEPS}
          currentStep={currentStep}
          onStepClick={handleStepClick}
        />

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 lg:p-8">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-gray-900">
                {STEPS[currentStep - 1].title}
              </h2>
              <p className="text-gray-500 mt-1">
                Korak {currentStep} od {STEPS.length}
              </p>
            </div>

            <div className="min-h-[400px]">{renderStep()}</div>

            <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-100">
              <button
                type="button"
                onClick={handleBack}
                disabled={currentStep === 1}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Natrag
              </button>

              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setShowPreview(true)}
                  className="lg:hidden inline-flex items-center px-4 py-2 text-sm font-medium text-teal-600 bg-teal-50 rounded-lg hover:bg-teal-100 transition-colors"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  Pregled
                </button>

                {currentStep < STEPS.length ? (
                  <button
                    type="button"
                    onClick={handleNext}
                    className="inline-flex items-center px-6 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 transition-colors"
                  >
                    Dalje
                    <svg className="w-4 h-4 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={loading}
                    className="inline-flex items-center px-6 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {loading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Kreiranje...
                      </>
                    ) : (
                      <>
                        {formData.id ? "Spremi promjene" : "Kreiraj pozivnicu"}
                        <svg className="w-4 h-4 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="hidden lg:block">
            <div className="sticky top-6">
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-medium text-gray-700">Pregled pozivnice</h3>
                  <span className="text-xs text-gray-400">Uživo ažuriranje</span>
                </div>
                <div className="rounded-xl overflow-hidden border border-gray-200">
                  <InvitationPreview
                    rsvp={formData}
                    isEditable={false}
                    onUpdate={updateFormData}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showPreview && (
        <div className="fixed inset-0 z-50 bg-black/50 lg:hidden">
          <div className="absolute inset-x-4 top-4 bottom-4 bg-white rounded-2xl overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="font-medium">Pregled pozivnice</h3>
              <button
                onClick={() => setShowPreview(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="h-[calc(100%-64px)] overflow-auto">
              <InvitationPreview
                rsvp={formData}
                isEditable={false}
                onUpdate={updateFormData}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvitationWizard;
